import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, ShieldCheck } from 'lucide-react';
import moment from 'moment';

import {
  useOrgReviewDecisionMutation,
  useOrgReviewQueueQuery,
} from '@/client/api/admin/organizations';
import type { AdminOrganization } from '@/client/types/admin/users-orgs';

import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

const PAGE_SIZE = 20;

const ModerationQueuePage = () => {
  useBreadcrumbs([
    { label: 'Organizations', href: '/admin/organizations' },
    { label: 'Moderation queue' },
  ]);

  const navigate = useNavigate();

  // Page number persists in the URL (?page=) so a reload keeps your place.
  const { page, handlePageChange } = useListFilters({
    initialFilters: {},
    initialLimit: PAGE_SIZE,
  });
  const { data, isLoading, isError } = useOrgReviewQueueQuery({
    page,
    page_size: PAGE_SIZE,
  });

  const decisionMutation = useOrgReviewDecisionMutation();

  const [decision, setDecision] = useState<{
    org: AdminOrganization;
    approved: boolean;
  } | null>(null);
  const [reason, setReason] = useState('');

  const organizations = data?.organizations ?? [];

  const submitDecision = async () => {
    if (!decision) return;
    if (!decision.approved && !reason.trim()) return;

    const toastId = toast.loading(
      decision.approved ? 'Approving name…' : 'Rejecting name…'
    );
    try {
      await decisionMutation.mutateAsync({
        oid: decision.org.oid,
        approved: decision.approved,
        reason: decision.approved ? undefined : reason.trim(),
      });
      toast.success(decision.approved ? 'Name approved' : 'Name rejected', {
        id: toastId,
        description: decision.approved
          ? `${decision.org.name} is now publicly listable.`
          : `${decision.org.name} stays in the queue with your reason recorded. It can still transact unless you place a hold.`,
        richColors: true,
        duration: decision.approved ? 4000 : 8000,
      });
      setDecision(null);
      setReason('');
    } catch (error: any) {
      toast.error(decision.approved ? 'Approval failed' : 'Rejection failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
              Name moderation queue
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Organizations awaiting a decision on their name, oldest first. Until approved they
              are not publicly listable.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin/organizations">All organizations</Link>
          </Button>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Approving makes an organization publicly listable. Rejecting records your reason and
          leaves it in this queue — it does <span className="font-semibold">not</span> stop the
          organization from transacting. To do that, place an admin hold from the organization&apos;s
          own page.
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Organization', 'Submitted', 'Last decision', ''].map((heading, index) => (
                  <th
                    key={heading || index}
                    className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                      heading === '' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    {Array.from({ length: 4 }).map((__, cell) => (
                      <td key={cell} className="px-6 py-4">
                        <Skeleton className="h-4 w-28" />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load the review queue. Try again.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && organizations.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                    Nothing is awaiting moderation.
                  </td>
                </tr>
              )}

              {organizations.map((org) => (
                <tr key={org.oid} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      className="flex items-center gap-3 text-left"
                      onClick={() => navigate(`/admin/organizations/${org.oid}`)}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-gray-900">{org.name}</span>
                        <span className="block text-xs text-gray-500">oid {org.oid}</span>
                      </span>
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {org.date_review_changed
                      ? moment(org.date_review_changed).format('YYYY-MM-DD HH:mm')
                      : org.date_added
                        ? moment(org.date_added).format('YYYY-MM-DD HH:mm')
                        : '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{org.review_reason || '—'}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setReason('');
                          setDecision({ org, approved: true });
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600"
                        onClick={() => {
                          setReason('');
                          setDecision({ org, approved: false });
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
            <Pagination
              total={data?.total ?? 0}
              page={page}
              limit={PAGE_SIZE}
              setPage={handlePageChange}
              showButtons
            />
          </div>
        </div>
      </div>

      <AlertDialog
        open={Boolean(decision)}
        onOpenChange={(next) => {
          if (!next) {
            setDecision(null);
            setReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {decision?.approved ? 'Approve this name?' : 'Reject this name?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {decision?.approved
                ? `${decision?.org.name} becomes publicly listable. Its hold and active state are unchanged.`
                : `${decision?.org.name} stays in this queue and is not publicly listable. Rejecting does not stop it transacting — use an admin hold for that.`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {decision && !decision.approved && (
            <div className="space-y-1">
              <Label htmlFor="reject-reason">
                Reason <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reject-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this name being rejected?"
              />
              <p className="text-xs text-gray-500">Recorded against the organization.</p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={
                decision && !decision.approved ? 'bg-red-600 hover:bg-red-700' : undefined
              }
              disabled={
                decisionMutation.isPending ||
                Boolean(decision && !decision.approved && !reason.trim())
              }
              onClick={(e) => {
                e.preventDefault();
                void submitDecision();
              }}
            >
              {decisionMutation.isPending
                ? 'Working…'
                : decision?.approved
                  ? 'Approve'
                  : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModerationQueuePage;
